import {
  createFeatureFlagService,
  type FeatureFlagService,
} from "../../services/feature-flags";
import {
  CALIBRATION_PASSAGE,
  calculateCalibration,
  createBrowserSpeechOutService,
  loadVoiceOptions,
  preferredOfflineVoice,
  targetWordsPerMinute,
  type DeviceVoice,
  type SpeechSynthesisAdapter,
  type SpeechUtterance,
} from "../../services/speech";
import {
  classificationDetail,
  sharedEventLog,
  type EventLog,
  type LogDetail,
} from "../../services/event-log";
import { createVersionedStorageService } from "../../services/versioned-storage";
import {
  decideAutomaticWorkflow,
  describeAutomaticWorkflow,
  REGISTRATION_UNAVAILABLE,
  type AutomaticWorkflowDecision,
} from "./automatic-orders";
import { mountOrdersDelivery, type DeliverySpeechSetup } from "./delivery-ui";
import {
  mountTraceGridRegistration,
  recognizeTraceGridCandidates,
  type GridFillTarget,
} from "./grid-recognition";
import {
  createPreflightSession,
  unfilledBlankCount,
  preflightStorageKey,
  savePreflightValues,
  type PreflightSession,
} from "./preflight";
import type { BlankFieldType } from "./normalization";
import {
  FRENCH_IGNORED_LINE,
  resolveOrderSetSelection,
  selectSerialFromFolder,
} from "./order-set";
import type { OrderSetFileInput } from "./order-set/types";
import { mountTracePicker, type TraceAssetSelection } from "./trace";

/**
 * Decides whether the trainee is shown an automatic result or the manual
 * recovery path. No local trace-to-basemap matcher exists yet, so registration
 * is reported as unavailable in every build and the manual controls stay
 * visible. Handover step 6 supplies the matcher behind the `automaticOrders`
 * flag; nothing else here has to change when it does.
 */
function workflowDecision(
  session: PreflightSession,
  values: Readonly<Record<string, string>>,
): AutomaticWorkflowDecision {
  return decideAutomaticWorkflow(
    REGISTRATION_UNAVAILABLE,
    session.blanks.map((blank) => ({
      fieldType: blank.fieldType,
      id: blank.id,
      required: blank.required,
      resolved: (values[blank.id] ?? blank.value).trim() !== "",
    })),
  );
}

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function band(title: string): HTMLElement {
  const section = element("section");
  section.className = "preflight-band";
  section.append(element("h3", title));
  return section;
}

function valuesFromForm(
  form: HTMLFormElement,
): Readonly<Record<string, string>> {
  return Object.fromEntries(
    Array.from(form.elements)
      .filter(
        (control): control is HTMLInputElement =>
          control instanceof HTMLInputElement,
      )
      .filter(({ name }) => name !== "")
      .map(({ name, value }) => [name, value]),
  );
}

/**
 * Which voices have a calibration stored on this device.
 *
 * Read straight from the key space rather than from the service, because
 * the question being answered is "what is actually on this device", and a
 * record that fails to parse still answers it.
 */
function storedCalibrationVoiceIds(): readonly string[] {
  const prefix = preflightStorageKey("calibration", "");
  const found: string[] = [];
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(prefix) === true) {
        found.push(key.slice(prefix.length));
      }
    }
  } catch {
    // Storage can throw in a private window. An empty answer is still an
    // answer, and the caller words it as one.
    return [];
  }
  return found.sort();
}

function mountCalibration(
  target: HTMLElement,
  storage: ReturnType<typeof createVersionedStorageService>,
  flags: FeatureFlagService,
  onSetup: (setup: DeliverySpeechSetup | null) => void,
  diagnostics: EventLog,
): () => void {
  if (!flags.isEnabled("speechOutput")) {
    target.append(element("p", "Speech output is disabled in this build."));
    onSetup(null);
    return () => undefined;
  }
  if (
    !("speechSynthesis" in window) ||
    typeof SpeechSynthesisUtterance === "undefined"
  ) {
    target.append(
      element(
        "p",
        "Speech synthesis is unavailable. On Brave, confirm that Shields is not blocking the browser speech service.",
      ),
    );
    onSetup(null);
    return () => undefined;
  }

  const nativeSynthesis = window.speechSynthesis;
  const synthesis: SpeechSynthesisAdapter = {
    cancel: () => nativeSynthesis.cancel(),
    getVoices: () => nativeSynthesis.getVoices(),
    pause: () => nativeSynthesis.pause(),
    resume: () => nativeSynthesis.resume(),
    speak: (utterance) =>
      nativeSynthesis.speak(utterance as unknown as SpeechSynthesisUtterance),
  };
  const speech = createBrowserSpeechOutService(
    synthesis,
    (text) => new SpeechSynthesisUtterance(text) as unknown as SpeechUtterance,
    {
      clearInterval: (handle) => window.clearInterval(handle),
      clearTimeout: (handle) => window.clearTimeout(handle),
      setInterval: (callback, delayMs) => window.setInterval(callback, delayMs),
      setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
    },
    {
      get hidden() {
        return document.hidden;
      },
      addEventListener: (_, listener) =>
        document.addEventListener("visibilitychange", listener),
      removeEventListener: (_, listener) =>
        document.removeEventListener("visibilitychange", listener),
    },
  );

  const voiceSelect = element("select");
  voiceSelect.dataset.role = "speech-voice";
  voiceSelect.disabled = true;
  const modeSelect = element("select");
  modeSelect.dataset.role = "speech-rate-mode";
  for (const [value, label] of [
    ["copy", "Copy - 90 wpm"],
    ["standard", "Standard - 100 wpm"],
    ["pressure", "Pressure - 130 to 180 wpm"],
    ["free", "Free - 80 to 180 wpm"],
  ] as const) {
    const option = element("option", label);
    option.value = value;
    modeSelect.append(option);
  }
  modeSelect.value = "standard";
  const rate = element("input");
  rate.type = "range";
  rate.min = "80";
  rate.max = "180";
  rate.value = "100";
  rate.disabled = true;
  rate.dataset.role = "speech-rate";
  const rateValue = element("output", "100 wpm");
  // Ruling 64f: it measures again rather than for the first time, because
  // delivery measures on its own.
  const calibrate = element("button", "Measure this voice again");
  calibrate.type = "button";
  calibrate.disabled = true;
  calibrate.dataset.role = "speech-calibrate";
  const status = element("p", "Loading voices reported by this device...");
  status.dataset.role = "speech-calibration-status";

  const updateRate = (): void => {
    const mode = modeSelect.value;
    if (mode === "copy" || mode === "standard") {
      const fixed = targetWordsPerMinute(mode);
      rate.value = String(fixed);
      rate.disabled = true;
    } else {
      rate.disabled = false;
      rate.min = mode === "pressure" ? "130" : "80";
      if (Number(rate.value) < Number(rate.min)) rate.value = rate.min;
    }
    rateValue.value = `${rate.value} wpm`;
    rateValue.textContent = rateValue.value;
  };
  const showSavedCalibration = (): void => {
    const selected = voiceSelect.value;
    const loaded = storage.load(preflightStorageKey("calibration", selected));
    const data = loaded.ok ? loaded.value.data : null;
    if (
      data === null ||
      !("voiceId" in data) ||
      !("wordsPerMinute" in data) ||
      data.voiceId !== selected
    ) {
      // Finding 4's diagnostic. Four candidate causes were eliminated by
      // measurement — it is written, it is read, the schema round-trips,
      // and a record survives a reload on the deployed origin — and the
      // remaining three could not be told apart from a developer machine.
      // The panel now reports the key it looked for and the keys that
      // exist, so one launch names the cause instead of another guess.
      const stored = storedCalibrationVoiceIds();
      // Ruling 50, and 2c: which of the three cases, the voice id sought,
      // and how many ids hold records. A voice id is not order content.
      diagnostics.append({
        module: "calibration",
        action: "lookup-missed",
        message:
          stored.length === 0
            ? "No calibration is stored for any voice."
            : stored.includes(selected)
              ? "A record exists for this voice but could not be read back."
              : "Records exist, but for other voice ids.",
        detail: {
          voiceIdSought: selected,
          storedCalibrations: stored.length,
          recordForThisVoice: stored.includes(selected),
        },
      });
      status.textContent =
        stored.length === 0
          ? `Rate not yet measured for this voice. Delivery will measure it as it speaks. ATLas is using "${selected}".`
          : stored.includes(selected)
            ? `A stored measurement for "${selected}" could not be read back, so delivery will measure it again as it speaks.`
            : `Rate not yet measured for "${selected}". This device has measurements for ${String(stored.length)} other voice${stored.length === 1 ? "" : "s"}. Delivery will measure this one as it speaks.`;
      // Ruling 64a, 12 October 2026: **nothing waits on calibration.** A
      // voice with no stored measurement still gives a usable setup —
      // multiplier 1 — and delivery measures itself as it speaks. This used
      // to hand back null, which disabled Start and left the trainee stuck
      // behind a button that did not work in his browser.
      onSetup({
        baseMultiplier: 1,
        measured: false,
        speech,
        targetWpm: targetWordsPerMinute(
          modeSelect.value as "copy" | "free" | "pressure" | "standard",
          Number(rate.value),
        ),
        voiceId: selected,
        voiceLabel:
          voiceSelect.selectedOptions.item(0)?.textContent ?? selected,
      });
      return;
    }
    const targetWpm = targetWordsPerMinute(
      modeSelect.value as "copy" | "free" | "pressure" | "standard",
      Number(rate.value),
    );
    const calibration = calculateCalibration(
      3_600_000 / data.wordsPerMinute,
      targetWpm,
    );
    diagnostics.append({
      module: "calibration",
      action: "lookup-hit",
      message: "A stored calibration was found and used.",
      detail: {
        voiceIdSought: selected,
        reachable: calibration.reachable,
        targetWpm,
      },
    });
    status.textContent = calibration.reachable
      ? `Calibrated at ${data.wordsPerMinute.toFixed(1)} wpm; ${String(targetWpm)} wpm uses a ${calibration.clampedMultiplier.toFixed(2)} multiplier.`
      : `Finding: this voice cannot reach ${String(targetWpm)} wpm within the 0.5 to 2.0 multiplier clamp.`;
    // Even an unreachable rate yields a setup: delivery proceeds at the
    // clamp and says so, rather than refusing to start. Ruling 64a.
    onSetup({
      baseMultiplier: calibration.clampedMultiplier,
      baselineWpm: data.wordsPerMinute,
      measured: true,
      speech,
      targetWpm,
      voiceId: selected,
      voiceLabel: voiceSelect.selectedOptions.item(0)?.textContent ?? selected,
    });
  };
  modeSelect.addEventListener("change", () => {
    updateRate();
    showSavedCalibration();
  });
  rate.addEventListener("input", () => {
    updateRate();
    showSavedCalibration();
  });
  voiceSelect.addEventListener("change", showSavedCalibration);
  calibrate.addEventListener("click", () => {
    const voiceId = voiceSelect.value;
    const selected = nativeSynthesis
      .getVoices()
      .find((voice) => voice.voiceURI === voiceId && voice.localService);
    if (selected === undefined) {
      status.textContent = "Choose a local voice before calibration.";
      return;
    }
    const mode = modeSelect.value as "copy" | "free" | "pressure" | "standard";
    const targetWpm = targetWordsPerMinute(mode, Number(rate.value));
    let startedAt: number | undefined;
    calibrate.disabled = true;
    onSetup(null);
    status.textContent = "Calibration passage is playing.";
    // So the export shows a press that produced nothing at all, which is
    // what his 12 October report could not distinguish from never pressing.
    diagnostics.append({
      module: "calibration",
      action: "calibration-started",
      message: "The calibration passage was started.",
      detail: { voiceId, mode, targetWpm },
    });
    const operation = speech.speak({
      text: CALIBRATION_PASSAGE,
      voiceId,
      rate: 1,
      onStart: () => {
        startedAt = performance.now();
      },
    });
    if (!(operation instanceof Promise)) {
      status.textContent = "Speech output is unavailable in this build.";
      calibrate.disabled = false;
      return;
    }
    void operation
      .then(() => {
        if (startedAt === undefined) {
          // The voice spoke and finished but never reported when it began,
          // so there is no elapsed time to divide by. Firefox does this for
          // some SAPI voices. Say what to do, not what went wrong inside.
          throw new Error(
            "This voice finished but never reported when it started, so its rate could not be measured. Choose another local voice and calibrate that one.",
          );
        }
        const result = calculateCalibration(
          performance.now() - startedAt,
          targetWpm,
        );
        diagnostics.append({
          module: "calibration",
          action: "calibration-measured",
          message: "The passage finished and a rate was measured.",
          detail: {
            voiceId,
            baselineWpm: Math.round(result.baselineWpm),
            targetWpm,
            reachable: result.reachable,
          },
        });
        const saved = storage.save(
          "calibration",
          preflightStorageKey("calibration", voiceId),
          { voiceId, wordsPerMinute: result.baselineWpm },
        );
        if (!saved.ok) throw new Error(saved.error.message);
        showSavedCalibration();
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error ? error.message : "Calibration failed.";
        status.textContent = message;
        status.dataset.status = "error";
        // Ruling 50: a rejection records why in the log, not only on screen.
        // His export of 12 October carried no calibration event at all.
        diagnostics.append({
          module: "calibration",
          action: "calibration-failed",
          message,
          detail: { voiceId },
        });
      })
      .finally(() => {
        calibrate.disabled = false;
      });
  });

  target.append(
    element("label", "Voice "),
    voiceSelect,
    element("label", " Rate mode "),
    modeSelect,
    rate,
    rateValue,
    calibrate,
    status,
  );
  const voiceSource = {
    addEventListener: (_: "voiceschanged", listener: () => void) =>
      nativeSynthesis.addEventListener("voiceschanged", listener),
    getVoices: (): readonly DeviceVoice[] => nativeSynthesis.getVoices(),
    removeEventListener: (_: "voiceschanged", listener: () => void) =>
      nativeSynthesis.removeEventListener("voiceschanged", listener),
  };
  void loadVoiceOptions(voiceSource, {
    clearTimeout: (handle) => window.clearTimeout(handle),
    setTimeout: (callback, delayMs) => window.setTimeout(callback, delayMs),
  }).then((voices) => {
    voiceSelect.replaceChildren();
    for (const voice of voices) {
      const option = element("option", voice.label);
      option.value = voice.id;
      option.disabled = !voice.isLocal;
      voiceSelect.append(option);
    }
    // Finding 2c of 8 October 2026. His export held a calibration record and
    // the panel still asked him to calibrate. The cause: the panel selected
    // the voice `preferredOfflineVoice` ranks first — local, then default,
    // then by name — and looked up *that* voice, while the record belonged
    // to whichever voice he had actually calibrated. A voice this device has
    // already calibrated now outranks the general preference.
    const stored = new Set(storedCalibrationVoiceIds());
    const alreadyCalibrated = voices.find(
      (voice) => voice.isLocal && stored.has(voice.id),
    );
    const preferred = alreadyCalibrated ?? preferredOfflineVoice(voices);
    if (preferred === null) {
      status.textContent =
        "Finding: this device reports no local speech voice. Network voices cannot be used offline.";
      diagnostics.append({
        module: "calibration",
        action: "no-local-voice",
        message: "This device reports no local speech voice.",
        detail: {
          voicesOffered: voices.length,
          storedCalibrations: stored.size,
        },
      });
      onSetup(null);
      return;
    }
    diagnostics.append({
      module: "calibration",
      action: "voice-selected",
      message:
        alreadyCalibrated === undefined
          ? "No offered voice had a stored calibration; took the ranked preference."
          : "Took the voice this device has already calibrated.",
      detail: {
        voiceId: preferred.id,
        fromStoredRecord: alreadyCalibrated !== undefined,
        voicesOffered: voices.length,
        localVoices: voices.filter(({ isLocal }) => isLocal).length,
        storedCalibrations: stored.size,
      },
    });
    voiceSelect.value = preferred.id;
    voiceSelect.disabled = false;
    calibrate.disabled = false;
    showSavedCalibration();
  });
  return () => {
    speech.cancel();
    speech.dispose();
  };
}

/**
 * What each kind of blank wants, in words the trainee uses.
 *
 * The field type is the classifier's own vocabulary; "unknown" in
 * particular is a statement about the classifier, not an instruction to
 * the trainee, so it is never put in front of him as one.
 */
const WANTED: Readonly<Record<BlankFieldType, string>> = {
  callsign: "callsign",
  "callsign-prefix": "callsign prefix",
  "control-measure": "control measure",
  formation: "formation",
  frequency: "frequency",
  grid: "six-figure grid",
  "h-hour": "H hour",
  "notice-to-move": "notice to move, in minutes",
  "squadron-designator": "squadron designator",
  time: "time",
  unknown: "value",
};

/**
 * The calibration the module holds across documents.
 *
 * Ruling 49 of 7 October 2026: calibration is a property of the voice and
 * the trainee, not of a document. It used to be mounted inside
 * `renderSession`, so it did not exist until a document was loaded, and it
 * was torn down and rebuilt every time he took the other document of the
 * same serial. It is now mounted once, when the module mounts, and each
 * document's pre-flight reads it and is told when it changes.
 */
interface CalibrationHolder {
  current(): DeliverySpeechSetup | null;
  dispose(): void;
  subscribe(listener: () => void): () => void;
}

function holdCalibration(
  target: HTMLElement,
  storage: ReturnType<typeof createVersionedStorageService>,
  flags: FeatureFlagService,
  diagnostics: EventLog,
): CalibrationHolder {
  let setup: DeliverySpeechSetup | null = null;
  const listeners = new Set<() => void>();
  const cleanup = mountCalibration(
    target,
    storage,
    flags,
    (next) => {
      setup = next;
      for (const listener of listeners) listener();
    },
    diagnostics,
  );
  return {
    current: () => setup,
    dispose: cleanup,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

interface SessionHandle {
  /** Begin delivery now. Returns what still needs him, or null if it began. */
  begin(): string | null;
  dispose(): void;
}

/**
 * What the Start panel says when the loaded document is not complete.
 *
 * Ruling 69b: one line, naming no blank and no value. Ruling 70 keeps the
 * completed values a surprise, so this cannot hint at what is missing or
 * what it would say — only that the directing staff have not finished with
 * this set and where the finished one is.
 */
const INCOMPLETE_SET_LINE =
  'This order set has not been completed by the DS yet, so it will not be delivered. Load the completed serial from its "Completed by Capt Holloway" folder.';

function renderSession(
  target: HTMLElement,
  session: PreflightSession,
  storage: ReturnType<typeof createVersionedStorageService>,
  flags: FeatureFlagService,
  traceSelection: () => TraceAssetSelection,
  diagnostics: EventLog,
  calibration: CalibrationHolder,
  onComplete: () => void,
): SessionHandle {
  target.replaceChildren();

  const fileBand = band("File");
  fileBand.append(
    element(
      "p",
      // Finding 2d: "1 paragraphs parsed" read as a near-total failure
      // while the log already recorded 22 items. The items are what show
      // the content is there, so they are named first and in plain words.
      `${String(session.parseSummary.itemCount)} item${session.parseSummary.itemCount === 1 ? "" : "s"} parsed across ${String(session.parseSummary.paragraphCount)} section${session.parseSummary.paragraphCount === 1 ? "" : "s"}. ${session.parseSummary.warningCount === 0 ? "No parser warnings." : `${String(session.parseSummary.warningCount)} parser warning${session.parseSummary.warningCount === 1 ? "" : "s"}.`}`,
    ),
  );
  if (session.issues.length > 0 || session.parserWarnings.length > 0) {
    const issues = element("ul");
    for (const issue of session.issues)
      issues.append(element("li", issue.message));
    for (const warning of session.parserWarnings)
      issues.append(element("li", warning.message));
    fileBand.append(issues);
  }

  const blanksBand = band("Blanks");
  const form = element("form");
  const requiredStatus = element("p");
  requiredStatus.dataset.role = "required-fill-status";
  let refreshStart = (): void => undefined;
  const gridInputs: HTMLInputElement[] = [];
  const fillTargets: GridFillTarget[] = [];
  for (const blank of session.blanks) {
    // The row names where it sits, quotes its own line with the blank
    // marked, and says in plain words what goes in and whether it has to.
    // It used to read "control measure 3 (control-measure)", which
    // identifies nothing; forty such rows read as forty blank rows.
    const label = element("label");
    const where = element("span", blank.location);
    where.className = "blank-location";
    // Shown only when the document gave the field its own name. Without one
    // the name falls back to the quoted line, and printing that twice tells
    // the trainee nothing he did not already read.
    if (blank.prompt !== blank.line) {
      const named = element("span", blank.prompt);
      named.className = "blank-name";
      label.append(where, named);
    } else {
      label.append(where);
    }
    const quoted = element("span", blank.line);
    quoted.className = "blank-line";
    const wants = element(
      "span",
      `${WANTED[blank.fieldType]}${blank.required ? ", required" : ", optional"}`,
    );
    wants.className = "blank-wants";
    label.append(quoted, wants);
    const input = element("input");
    input.name = blank.id;
    input.value = blank.value;
    input.autocomplete = "off";
    input.required = blank.required;
    input.dataset.blankId = blank.id;
    if (blank.fieldType === "grid") gridInputs.push(input);
    if (blank.required) {
      fillTargets.push({
        input,
        isKnownGrid: blank.fieldType === "grid",
        prompt: blank.prompt,
      });
    }
    label.append(input);
    form.append(label);
  }
  if (session.blanks.length === 0)
    form.append(element("p", "No blanks detected."));
  form.addEventListener("input", () => {
    const issue = savePreflightValues(
      storage,
      "fills",
      session.fileHash,
      valuesFromForm(form),
    );
    if (issue !== null) form.dataset.storageError = issue.message;
    else delete form.dataset.storageError;
    refreshStart();
  });
  const gridAssist = element("div");
  gridAssist.className = "grid-assist";
  const workflowStatus = element("p");
  workflowStatus.dataset.role = "grid-workflow-status";
  gridAssist.append(workflowStatus);
  const renderWorkflowStatus = (
    values: Readonly<Record<string, string>>,
  ): AutomaticWorkflowDecision => {
    const decision = workflowDecision(session, values);
    workflowStatus.textContent = describeAutomaticWorkflow(decision);
    workflowStatus.dataset.mode = decision.mode;
    return decision;
  };
  const initialDecision = renderWorkflowStatus({});
  const gridCandidates = element("datalist");
  gridCandidates.id = `grid-candidates-${session.fileHash.slice(0, 12)}`;
  for (const input of gridInputs) input.setAttribute("list", gridCandidates.id);
  const shouldOfferGridScan =
    gridInputs.length > 0 || session.documentKind === "warning-order";
  if (shouldOfferGridScan) {
    const scan = element("button", "Scan trace for grid references");
    scan.type = "button";
    scan.dataset.role = "grid-scan";
    const scanStatus = element(
      "p",
      "OCR candidates are local suggestions. Confirm every grid against the trace before using it.",
    );
    scanStatus.dataset.role = "grid-scan-status";
    // Finding B: a button that cannot work must not be pressable. It used
    // to be enabled with no trace image and only complain once pressed.
    // The scan is gated on gridRecognition, not on automaticOrders, which
    // is why it was live in a build reporting automaticOrders false.
    // Availability only. Kept separate from the status text because the
    // text may be holding a scan result the trainee needs to read, and an
    // idle hint written over it loses the result.
    const refreshScanAvailability = (): void => {
      scan.disabled =
        !flags.isEnabled("gridRecognition") ||
        traceSelection().imageFile === null;
    };
    const refreshScan = (): void => {
      const enabled = flags.isEnabled("gridRecognition");
      const hasImage = traceSelection().imageFile !== null;
      refreshScanAvailability();
      if (!enabled) {
        scanStatus.textContent =
          "Local grid recognition is disabled in this build.";
        scanStatus.dataset.status = "error";
      } else if (!hasImage) {
        scanStatus.textContent =
          "Waiting for a trace image. Load an order set, which supplies one.";
        scanStatus.dataset.status = "error";
      } else {
        scanStatus.textContent =
          "OCR candidates are local suggestions. Confirm every grid against the trace before using it.";
        delete scanStatus.dataset.status;
      }
    };
    refreshScan();
    scan.addEventListener("click", () => {
      const image = traceSelection().imageFile;
      /* v8 ignore next 8 -- refreshScan disables the button without one */
      if (image === null) {
        diagnostics.append({
          module: "grid-recognition",
          action: "scan-refused",
          message: "No trace image is loaded, so the scan did not run.",
        });
        refreshScan();
        return;
      }
      diagnostics.append({
        module: "grid-recognition",
        action: "scan-started",
        message: "A local grid scan of the trace image started.",
      });
      scan.disabled = true;
      scanStatus.textContent = "Loading the local OCR engine...";
      delete scanStatus.dataset.status;
      void recognizeTraceGridCandidates(image, ({ progress, status }) => {
        scanStatus.textContent = `${status}: ${String(Math.round(progress * 100))}%`;
      })
        .then(({ candidates, confidence }) => {
          gridCandidates.replaceChildren();
          const candidateList = element("ul");
          candidateList.dataset.role = "grid-candidates";
          for (const candidate of candidates) {
            const option = element("option");
            option.value = candidate.value;
            option.label = candidate.label;
            gridCandidates.append(option);
            candidateList.append(element("li", candidate.label));
          }
          const confidenceText = `${String(Math.round(confidence))}% page confidence`;
          scanStatus.textContent =
            candidates.length === 0
              ? `No explicit grid references were found (${confidenceText}). Enter them manually from the trace.`
              : `${String(candidates.length)} possible grid reference${candidates.length === 1 ? "" : "s"} found (${confidenceText}). Confirm visually, then choose or type the value in each grid field.`;
          scanStatus.dataset.status =
            candidates.length === 0 ? "error" : "ready";
          diagnostics.append({
            module: "grid-recognition",
            action: "scan-finished",
            message: "The local grid scan finished.",
            detail: {
              candidates: candidates.length,
              confidence: Math.round(confidence),
            },
          });
          // By role. An unqualified "ul" removed whichever list came
          // first in the band, which need not have been this one.
          gridAssist.querySelector('ul[data-role="grid-candidates"]')?.remove();
          if (candidates.length > 0) gridAssist.append(candidateList);
        })
        .catch((error: unknown) => {
          scanStatus.textContent =
            "The local grid scan failed. Enter the grids manually from the trace.";
          scanStatus.dataset.status = "error";
          diagnostics.append({
            module: "grid-recognition",
            action: "scan-failed",
            message:
              error instanceof Error
                ? error.message
                : "The local grid scan failed.",
          });
        })
        .finally(refreshScanAvailability);
    });
    gridAssist.append(scan, scanStatus, gridCandidates);
    // Finding B. This offered manual four-point registration, which Ruling
    // 22 put out of iteration 1 and every block since has held parked. It
    // was gated on nothing at all: `manualGridRegistrationOffered` is
    // `!registrationAccepted || hasUnresolvedGrid`, and a parked
    // registration is never accepted, so the button appeared on every
    // document with a grid blank. Pressing it cleared any registration
    // already entered, and completing one wrote a computed grid straight
    // into his field.
    //
    // It then stayed on screen as a disabled button and a line of
    // explanation, on the reasoning that a control which vanishes is its own
    // puzzle. **Ruled against on 17 October 2026 (a), section 11 item 5:**
    // nothing of a feature whose flag is false reaches the screen, hard rule
    // 7. A dead control plus an instruction to do something there is nothing
    // to do is worse than silence, and the flag snapshot in the bug report
    // answers "is it off or broken" without putting anything on his screen.
    //
    // The log entry stays. It is not the screen, and it is how a workflow
    // decision that offered registration is still recoverable from an export.
    if (initialDecision.manualGridRegistrationOffered) {
      if (flags.isEnabled("gridRegistration")) {
        mountTraceGridRegistration(gridAssist, traceSelection, fillTargets);
      } else {
        diagnostics.append({
          module: "grid-recognition",
          action: "registration-offered-but-parked",
          message:
            "Manual grid registration was offered by the workflow decision, is disabled in this build, and nothing of it was put on screen.",
          detail: { mode: initialDecision.mode },
        });
      }
    }
  }
  blanksBand.append(requiredStatus, form, gridAssist);

  const lexiconBand = band("Unresolved tokens");
  if (session.unresolvedTokens.length === 0) {
    lexiconBand.append(element("p", "None."));
  } else {
    const list = element("ul");
    for (const token of session.unresolvedTokens) {
      const item = element("li", token);
      item.className = "unresolved-token";
      list.append(item);
    }
    lexiconBand.append(
      list,
      element("p", "These entries do not block playback."),
    );
  }

  // R1 Step 14. Its own band, behind its own flag, after delivery. It alters
  // no part of the delivery path.

  const precisionBand = band("Grid precision");
  if (session.gridPrecisionWarnings.length === 0) {
    precisionBand.append(
      element("p", "Every grid in this document is six-figure."),
    );
  } else {
    const list = element("ul");
    list.dataset.role = "grid-precision-warnings";
    for (const warning of session.gridPrecisionWarnings) {
      const entry = element("li", warning.message);
      entry.dataset.status = "error";
      list.append(entry);
    }
    precisionBand.append(
      list,
      element(
        "p",
        "These do not block delivery and are still spoken digit by digit.",
      ),
    );
  }

  const cuesBand = band("Cues");
  if (session.cues.length === 0) {
    // Finding C. The band rendered an empty list. Cues are read only from a
    // marker of the exact form <!-- CUE:EYES_UP --> in the document, and
    // neither of the trainee's documents carries one: his orders card has
    // no HTML comment at all. Doc 03 section 6.1 says PENS_RESUME binds to
    // the Main Effort item inside EXECUTION/CONOPS, "never to a bare text
    // match" — that is a structural binding, not an authored marker, and it
    // is not implemented. Reported, not changed here.
    cuesBand.append(
      element(
        "p",
        "No cues in this document. ATLas reads the pens-down and eyes-up cues from markers written into the card, and this card carries none, so delivery runs without them.",
      ),
    );
  } else {
    const cueList = element("ul");
    for (const cue of session.cues) {
      cueList.append(
        element(
          "li",
          `${cue.name}: ${cue.nodeId ?? "not available"} (${cue.source})`,
        ),
      );
    }
    cuesBand.append(cueList);
  }

  const start = element("button", "Start delivery");
  start.type = "button";
  start.disabled = true;
  start.dataset.preflightReady = String(session.canStartPlayback);
  start.dataset.role = "delivery-start";
  // A disabled control states why it is disabled, in visible text beside
  // itself. A tooltip is not visible on a touch device and a dead control
  // with no explanation is its own defect.
  const startReason = element("p");
  startReason.className = "start-reason";
  startReason.dataset.role = "delivery-start-reason";
  let deliveryCleanup = (): void => undefined;

  refreshStart = (): void => {
    // Ruling 63, 8 October 2026. **An unfilled blank never blocks delivery.**
    // No ruling ever made one required: the gate was a line of
    // implementation, and doc 04 section 6.4 says the opposite — an unfilled
    // blank is delivered as a token, by default silence then the word
    // "blank". The count is information, not a gate.
    const unfilled = unfilledBlankCount(session.blanks, valuesFromForm(form));
    const deliveryEnabled = flags.isEnabled("delivery");
    // Ruling 64a: only delivery being switched off can stop a run now.
    start.disabled = !deliveryEnabled;
    start.dataset.preflightReady = String(!start.disabled);
    start.dataset.unfilledBlanks = String(unfilled);
    requiredStatus.textContent =
      unfilled === 0
        ? `Every blank is filled. ${String(session.blanks.length)} in this document.`
        : // Ruling 69: no count of what is missing is useful to him and
          // none is offered. The set is not ready; that is the whole of it.
          INCOMPLETE_SET_LINE;
    // Information, never an error: nothing here stops him.
    requiredStatus.dataset.status = "info";
    renderWorkflowStatus(valuesFromForm(form));
    const reason = !deliveryEnabled
      ? "Orders delivery is disabled in this build."
      : null;
    startReason.textContent = reason ?? "";
    startReason.hidden = reason === null;
    startReason.dataset.status = reason === null ? "ready" : "error";
    start.title = reason ?? "Start delivery using the selected local voice.";
  };
  // The calibration band is not rebuilt here, so a change to it has to
  // reach this document's start button.
  const unsubscribe = calibration.subscribe(() => {
    refreshStart();
  });
  refreshStart();
  const begin = (): string | null => {
    refreshStart();
    const speechSetup = calibration.current();
    if (!flags.isEnabled("delivery")) {
      return "Orders delivery is disabled in this build.";
    }
    // Ruling 69b, 14 October 2026, amending Ruling 63. An order set that
    // reaches the trainee with a blank in it is wrong, so an incomplete
    // document does not start. The line names no blank and no value: it
    // says the set is not ready and where the ready one lives.
    if (unfilledBlankCount(session.blanks, valuesFromForm(form)) > 0) {
      return INCOMPLETE_SET_LINE;
    }
    if (speechSetup === null) {
      // Ruling 64a: not a step he has to take, so this is a device fault —
      // no local voice at all — and it is worded as one.
      return "This device reports no local speech voice, so there is nothing to deliver with.";
    }
    deliveryCleanup();
    deliveryCleanup = mountOrdersDelivery(target, {
      fills: valuesFromForm(form),
      flags,
      session,
      speechSetup,
      storage,
      traceSelection: traceSelection(),
      onComplete,
      // Ruling 64c: store it for that voice, so the next run starts
      // already measured.
      onMeasured: (state) => {
        if (state.baselineWpm === null) return;
        const key = preflightStorageKey("calibration", speechSetup.voiceId);
        const saved = storage.save("calibration", key, {
          voiceId: speechSetup.voiceId,
          wordsPerMinute: state.baselineWpm,
        });
        diagnostics.append({
          module: "calibration",
          action: "measured-during-delivery",
          message: saved.ok
            ? "The rate was measured during delivery and stored for this voice."
            : "The rate was measured during delivery but could not be stored.",
          detail: {
            voiceId: speechSetup.voiceId,
            baselineWpm: Math.round(state.baselineWpm),
            timedWords: state.timedWords,
            source: state.source,
            stored: saved.ok,
          },
        });
      },
    });
    return null;
  };
  start.addEventListener("click", () => {
    begin();
  });
  // Ruling 58b, 8 October 2026. One closed line holds every band that needs
  // nothing from the trainee. After Ruling 63 that is all of them: no blank
  // is required, so nothing here stops him and nothing here has to be read
  // before he starts. The trace scan and the dead registration control are
  // inside the Blanks band's grid assist, so they fold with it.
  const details = element("details");
  details.className = "preflight-details";
  details.dataset.role = "preflight-details";
  details.append(element("summary", "Pre-flight details"));
  details.append(fileBand, blanksBand, lexiconBand, precisionBand, cuesBand);
  // `start` is no longer appended: Ruling 58 replaces it with the Start
  // panel above, which is the one place a run begins. Its reason line stays,
  // because that is where "what needs him" is named.
  target.append(details, startReason);
  return {
    begin,
    dispose: () => {
      deliveryCleanup();
      unsubscribe();
    },
  };
}

export function mountOrdersReaderPreflight(container: HTMLElement): void {
  const controls = element("div");
  controls.className = "preflight";
  const output = element("div", "Load an order set above to begin pre-flight.");
  output.dataset.role = "preflight-output";
  const flags = createFeatureFlagService();
  // Ruling 49: calibration first, and once. It belongs to the voice and the
  // trainee, so it is here rather than inside each document's pre-flight.
  // Ruling 64f, 12 October 2026: calibration is no longer a step, so it is
  // no longer at the top of the screen. It folds away with everything else
  // that needs nothing from him.
  const calibrationBand = band("Voice");
  const traceBand = band("Trace");
  const tracePicker = mountTracePicker(traceBand, flags);

  // Change 2: one selection loads a whole serial. Loading is one action;
  // delivery is not, and nothing here starts one. Ruling 17 stands, so the
  // trainee still chooses which document to take and starts it himself.
  const setBand = band("Order set");

  // Ruling 71, 12 October 2026. One action: the folder. ATLas takes the
  // English warning order, the orders, the trace image and the sidecar out of
  // it, and says what it left behind.
  const folderLabel = element(
    "label",
    "Select the folder of one serial. ATLas takes the English warning order, " +
      "the orders, the trace image and the overlay from it. ",
  );
  const folderInput = element("input");
  folderInput.type = "file";
  folderInput.multiple = true;
  // The directory attributes are not in the HTML standard and are set
  // through the attribute rather than a typed property for that reason. A
  // browser that ignores them hands over the files the trainee picked and
  // the control still works; one that refuses the control altogether leaves
  // the file selection below, which is why that selection stays.
  folderInput.setAttribute("webkitdirectory", "");
  folderInput.setAttribute("directory", "");
  folderInput.dataset.role = "order-set-folder";
  folderLabel.append(folderInput);
  const folderStatus = element(
    "p",
    "One action. File selection below is the fallback.",
  );
  folderStatus.dataset.role = "order-set-folder-status";

  const setLabel = element(
    "label",
    "Or select the three files of one serial together: " +
      "the warning order (.md), the orders card (.md), and the trace image " +
      "(.jpg, .jpeg, .png or .webp). ",
  );
  const setInput = element("input");
  setInput.type = "file";
  setInput.multiple = true;
  // The control states the extensions it takes, and the file chooser
  // enforces them. A control that accepts anything and then complains is
  // asking the trainee to guess.
  setInput.accept =
    ".md,.markdown,text/markdown,text/plain," +
    ".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp";
  setInput.dataset.role = "order-set-files";
  setLabel.append(setInput);
  const setStatus = element(
    "p",
    "Three files, one action. Nothing below asks for any of them again.",
  );
  setStatus.dataset.role = "order-set-status";
  const setChoices = element("div");
  setChoices.className = "order-set-choices";
  setBand.append(folderLabel, folderStatus, setLabel, setStatus, setChoices);

  // Order set first, because it is the only thing the trainee has to supply.
  // The trace band is second and, once a set is loaded, asks only for the
  // optional overlay. The diagnostics band is the shell's, not this
  // module's: one log, one export, and the modules write into it.
  // Ruling 58: one Start panel. It replaces Step 1, Step 2 and the separate
  // Start delivery button, so there is one place a run begins.
  const startPanel = band("Start");
  startPanel.hidden = true;
  const startChoices = element("div");
  startChoices.className = "start-choices";
  startChoices.dataset.role = "start-choices";
  const startLine = element("p");
  startLine.dataset.role = "start-line";
  startPanel.append(startChoices, startLine);

  let session: SessionHandle | null = null;
  let pendingOrders: (() => void) | null = null;

  const voiceDetails = element("details");
  voiceDetails.className = "preflight-details";
  voiceDetails.dataset.role = "voice-details";
  voiceDetails.append(
    element("summary", "Pre-flight details"),
    calibrationBand,
  );

  controls.append(setBand, startPanel, traceBand, voiceDetails, output);
  container.append(controls);

  const storage = createVersionedStorageService(window.localStorage, () =>
    new Date().toISOString(),
  );
  // Ruling 50. One log for the module, so a refusal anywhere in the intake
  // is recoverable from the export rather than from the trainee's memory.
  const diagnostics = sharedEventLog();
  let sessionState: LogDetail = { loaded: false };
  diagnostics.append({
    module: "orders-reader",
    action: "module-mounted",
    message: "The Orders Reader mounted and is waiting for an order set.",
    detail: {
      automaticOrders: flags.isEnabled("automaticOrders"),
      delivery: flags.isEnabled("delivery"),
      estimatedGrids: flags.isEnabled("estimatedGrids"),
      phaseWalk: flags.isEnabled("phaseWalk"),
      trace: flags.isEnabled("trace"),
    },
  });
  const calibration = holdCalibration(
    calibrationBand,
    storage,
    flags,
    diagnostics,
  );

  const loadDocument = (file: File, autoStart = false): void => {
    output.textContent = "Parsing local file...";
    void file
      .text()
      .then(
        async (source) => await createPreflightSession(source, storage, flags),
      )
      .then((result) => {
        if (!result.ok) {
          output.textContent = result.error.message;
          output.dataset.status = "error";
          // Ruling 50: a rejection records why in the log, not only on
          // screen. The message is the application's own, never the file's.
          diagnostics.append({
            module: "orders-reader",
            action: "document-rejected",
            message: result.error.message,
            detail: { file: file.name, code: result.error.code },
          });
          return;
        }
        output.dataset.status = "ready";
        const loaded = result.session;
        const byType = new Map<string, number>();
        for (const blank of loaded.blanks) {
          byType.set(blank.fieldType, (byType.get(blank.fieldType) ?? 0) + 1);
          // Ruling 50's per-blank record, in the classification channel so
          // it cannot evict the operational events.
          diagnostics.classify(
            classificationDetail({
              blankId: blank.id,
              fieldType: blank.fieldType,
              line: blank.sourceLine,
              reason: blank.typing.reason,
              rule: blank.typing.rule,
              width: blank.width,
            }),
          );
        }
        sessionState = {
          loaded: true,
          documentKind: loaded.documentKind,
          fileHash: loaded.fileHash,
          paragraphs: loaded.parseSummary.paragraphCount,
          items: loaded.parseSummary.itemCount,
          parserWarnings: loaded.parseSummary.warningCount,
          blanks: loaded.blanks.length,
          required: loaded.blanks.filter(({ required }) => required).length,
          ...Object.fromEntries(
            [...byType.entries()].map(([type, n]) => [`type:${type}`, n]),
          ),
        };
        diagnostics.append({
          module: "orders-reader",
          action: "document-loaded",
          message: "A document was parsed and pre-flight built.",
          detail: sessionState,
        });
        session?.dispose();
        session = renderSession(
          output,
          loaded,
          storage,
          flags,
          () => tracePicker.selection(),
          diagnostics,
          calibration,
          () => {
            // Ruling 58c: the run is over, so offer the next document.
            if (pendingOrders === null) return;
            const offer = element("button", "Continue to the orders");
            offer.type = "button";
            offer.dataset.role = "continue-to-orders";
            offer.addEventListener("click", () => {
              pendingOrders?.();
            });
            startChoices.replaceChildren(offer);
            startLine.textContent =
              "The warning order is delivered. Ruling 45: the orders are a second run.";
          },
        );
        // Ruling 58a: start at once when nothing needs him.
        if (autoStart) {
          const needed = session.begin();
          startLine.textContent =
            needed ??
            "Delivering. Everything else is under Pre-flight details.";
          startLine.dataset.status = needed === null ? "ready" : "error";
        }
      })
      .catch((error: unknown) => {
        const message =
          error instanceof Error
            ? error.message
            : "The local file could not be read.";
        output.textContent = message;
        output.dataset.status = "error";
        diagnostics.append({
          module: "orders-reader",
          action: "document-read-failed",
          message,
          detail: { file: file.name },
        });
      });
  };

  /**
   * The language layer is read from the document content, per doc 04 section
   * 5.5, so the text has to be in hand before the selection can be resolved.
   * Only formats that carry text are read; an image contributes `null`.
   */
  const readSelection = async (
    chosen: readonly File[],
  ): Promise<readonly OrderSetFileInput[]> =>
    await Promise.all(
      chosen.map(async (file) => ({
        name: file.name,
        text: /\.(?:md|markdown)$/i.test(file.name)
          ? await file.text().catch(() => null)
          : null,
      })),
    );

  const resolveSelection = async (
    chosen: readonly File[],
    status: HTMLElement = setStatus,
  ): Promise<void> => {
    setChoices.replaceChildren();
    const inputs = await readSelection(chosen);
    const resolved = resolveOrderSetSelection(inputs);
    if (!resolved.ok) {
      status.textContent = resolved.message;
      status.dataset.status = "error";
      diagnostics.append({
        module: "order-set",
        action: "selection-refused",
        message: resolved.message,
        detail: { code: resolved.code, files: chosen.length },
      });
      return;
    }
    // Ruling 57: a completed document carries the **same file name** as the
    // practice card it was filled in from, so a name alone cannot find the
    // right `File` back. Match on the name and, where several share one, on
    // the blank count that told the intake which to take.
    const blanksOf = (text: string | null): number =>
      text === null ? 0 : (text.match(/_9C|_{2,}/g) ?? []).length;
    const fileFor = (want: {
      readonly blankCount: number;
      readonly name: string;
    }): File | undefined => {
      const sameName = chosen.filter((file) => file.name === want.name);
      if (sameName.length <= 1) return sameName[0];
      const match = sameName.find(
        (file) =>
          blanksOf(inputs[chosen.indexOf(file)]?.text ?? null) ===
          want.blankCount,
      );
      return match ?? sameName[0];
    };
    const { languageLayer, ordersCard, setId, traceImage, warningOrder } =
      resolved.set;
    status.textContent = `Set ${setId} loaded, ${languageLayer === "unmarked" ? "single language" : languageLayer.toUpperCase()}. Nothing is playing. The trace is held and is not shown here; it is issued during the run.`;
    status.dataset.status = "ready";
    // The set carries the trace image, so the trace band does not ask for it.
    // It used to be discarded here and asked for again below.
    const traceFile = fileFor(traceImage);
    tracePicker.adoptImage(traceFile ?? null);

    // Ruling 58, 8 October 2026. One Start panel, two ways in, and pressing
    // either begins the run. It replaces Step 1, Step 2 and the separate
    // Start delivery button: the sequence is still the warning order then
    // the orders (Ruling 45), but he no longer has to take a document and
    // then find a second control to start it.
    const ordersSource = fileFor(ordersCard);
    const warningSource =
      warningOrder === null ? undefined : fileFor(warningOrder);

    // Ruling 58c needs this to exist before the first run finishes.
    pendingOrders =
      ordersSource === undefined
        ? null
        : () => {
            loadDocument(ordersSource, true);
          };

    const entries: { label: string; role: string; file: File | undefined }[] =
      [];
    if (warningOrder !== null) {
      entries.push({
        label: "Start from the warning order",
        role: "start-from-warning-order",
        file: warningSource,
      });
    }
    entries.push({
      label: "Start from the orders",
      role: "start-from-orders",
      file: ordersSource,
    });

    startChoices.replaceChildren();
    for (const entry of entries) {
      const choice = element("button", entry.label);
      choice.type = "button";
      choice.dataset.role = entry.role;
      choice.disabled = entry.file === undefined;
      choice.addEventListener("click", () => {
        if (entry.file === undefined) return;
        loadDocument(entry.file, true);
      });
      startChoices.append(choice);
    }
    startPanel.hidden = false;
    // Ruling 57: say which documents are being delivered. A completed
    // document has no blanks, so every value in it is confirmed and nothing
    // is left for him to fill.
    const completedCount = [warningOrder, ordersCard].filter(
      (file) => file !== null && file.blankCount === 0,
    ).length;
    const which =
      completedCount === 2
        ? "Delivering the completed order set: every value is written in."
        : completedCount === 1
          ? "One document of this serial is completed and one still has blanks."
          : INCOMPLETE_SET_LINE;
    startLine.textContent =
      warningOrder === null
        ? // A missing warning order changes where battle procedure starts,
          // so it is said rather than left to be noticed.
          `This serial has no warning order, so delivery starts at the orders. ${which}`
        : `Battle procedure starts at the warning order. The orders are a second run. ${which}`;
    delete startLine.dataset.status;
  };

  const report = (status: HTMLElement, error: unknown): void => {
    // Doc 02 section 4: a failure surfaces, never silently.
    status.textContent =
      error instanceof Error
        ? error.message
        : "That selection could not be read.";
    status.dataset.status = "error";
  };

  setInput.addEventListener("change", () => {
    setStatus.textContent = "Reading the selection...";
    setStatus.dataset.status = "working";
    void resolveSelection(Array.from(setInput.files ?? [])).catch(
      (error: unknown) => {
        report(setStatus, error);
      },
    );
  });

  // Ruling 71. The folder path reads every file in the folder, picks the
  // serial out of it, and names what it did not take. The French half is set
  // aside in one line: the French serial needs a sidecar of its own, and the
  // English one describes different grids and different timings.
  folderInput.addEventListener("change", () => {
    folderStatus.textContent = "Reading the folder...";
    folderStatus.dataset.status = "working";
    const held = Array.from(folderInput.files ?? []);
    void (async () => {
      const inputs = await readSelection(held);
      const picked = selectSerialFromFolder(inputs);
      const keep = new Set(picked.selected.map(({ name }) => name));
      const sidecarName = picked.sidecar?.name ?? null;
      diagnostics.append({
        module: "order-set",
        action: "folder-selected",
        message: "A serial was picked out of a folder.",
        detail: {
          held: held.length,
          taken: picked.selected.length,
          ignored: picked.ignored.length,
          sidecar: sidecarName !== null,
          frenchPresent: picked.frenchPresent,
        },
      });
      if (sidecarName !== null) {
        const sidecarFile = held.find(({ name }) => name === sidecarName);
        tracePicker.adoptSidecar(sidecarFile ?? null);
      }
      await resolveSelection(
        held.filter(({ name }) => keep.has(name)),
        folderStatus,
      );
      if (folderStatus.dataset.status === "ready" && picked.frenchPresent) {
        folderStatus.textContent = `${folderStatus.textContent} ${FRENCH_IGNORED_LINE}`;
      }
      // File selection stays, and stays usable: it is the fallback for a
      // browser that will not open a folder at all.
      setStatus.textContent =
        "Loaded from the folder above. This control is the fallback and is still available.";
    })().catch((error: unknown) => {
      report(folderStatus, error);
    });
  });
}
